import React, { useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';

import getUserNameInitials from 'common/util/getUserNameInitials';

import { USER_ROLE_ADMIN } from 'common/userRoles';

import {
  MEDIA_QUERY_MEDIUM_SCREEN_750,
  MEDIA_QUERY_LARGE_SCREEN,
} from 'client/constants/mediaQueries';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrFlex from 'client/components/SrFlex';
import SrViewAsPartnerToggle from 'client/components/SrViewAsPartnerToggle';
import SrPartnerGroupAdminLinks from 'client/components/SrPartnerGroupAdminLinks/SrPartnerGroupAdminLinks';
import SrProgressBarPercent, {
  SR_PROGRESS_BAR_THEME_YELLOW,
} from 'client/components/SrProgressBarPercent/SrProgressBarPercent';

import './SrPartnerPageV2.less';
import {
  UTILITY_USER_ROLE_ADMIN,
  UTILITY_USER_ROLE_PARTNER,
  hasRoleGroupAdmin,
  hasRolePartnerPropertyManager,
} from 'common/utility/userRoles';

export const SR_PARTNER_PAGE_THEME_BLANK_STATE = 'SR_PARTNER_PAGE_THEME_BLANK_STATE';
export const SR_PARTNER_PAGE_THEME_BILLING = 'SR_PARTNER_PAGE_THEME_BILLING';
export const SR_PARTNER_PAGE_THEME_TO_CLASSNAME_MAPPING = {
  [SR_PARTNER_PAGE_THEME_BLANK_STATE]: 'srPartnerPageThemeBlankState',
  [SR_PARTNER_PAGE_THEME_BILLING]: 'srPartnerPageThemeBilling',
};
export const SR_PARTNER_PAGE_THEMES = Object.keys(SR_PARTNER_PAGE_THEME_TO_CLASSNAME_MAPPING);

export const SR_PARTNER_PAGE_NAVBAR_THEME_ROUNDED = 'SR_PARTNER_PAGE_NAVBAR_THEME_ROUNDED';
export const SR_PARTNER_PAGE_NAVBAR_THEME_TO_CLASSNAME_MAPPING = {
  [SR_PARTNER_PAGE_NAVBAR_THEME_ROUNDED]: 'navBarRounded',
};
export const SR_PARTNER_PAGE_NAVBAR_THEMES = Object.keys(
  SR_PARTNER_PAGE_NAVBAR_THEME_TO_CLASSNAME_MAPPING,
);

export const SR_PARTNER_PAGE_V2_FONT_SIZE_SMALL = 'SR_PARTNER_PAGE_V2_FONT_SIZE_SMALL';
export const SR_PARTNER_PAGE_V2_FONT_SIZE_MEDIUM = 'SR_PARTNER_PAGE_V2_FONT_SIZE_MEDIUM';
export const SR_PARTNER_PAGE_V2_FONT_SIZE_TO_CLASSNAME_MAPPING = {
  [SR_PARTNER_PAGE_V2_FONT_SIZE_SMALL]: 'font-size-small',
  [SR_PARTNER_PAGE_V2_FONT_SIZE_MEDIUM]: 'font-size-medium',
};
export const SR_PARTNER_PAGE_V2_FONT_SIZES = Object.keys(
  SR_PARTNER_PAGE_V2_FONT_SIZE_TO_CLASSNAME_MAPPING,
);

const SrPartnerPageV2 = ({
  className,
  children,
  backUrl,
  onBack,
  navbarImage,
  navbarImageLink,
  navbarTitle,
  navbarLinks,
  pageHeader,
  headerSize,
  progressBarPercent,
  progressBarText,
  theme,
  step,
  shouldScrollToTop,
  padding,
  contentPadding,
  maxWidth,
  navBarTheme,
  navBarActionButton,
  navbarMaxWidth,
  pageSubtitle,
  hasWatermark,
  currentUser,
  currentPartner,
  utilityUser,
  utilityPartner,
  redirectToLastLocation,
}) => {
  const isLargeScreen = useMediaQuery({
    query: MEDIA_QUERY_LARGE_SCREEN,
  });

  const isMediumScreen = useMediaQuery({
    query: MEDIA_QUERY_MEDIUM_SCREEN_750,
  });

  const location = useLocation();

  const showAccountInfo = currentUser || utilityUser;
  const showUtilityPartnerSettings =
    utilityPartner &&
    utilityPartner.slug &&
    utilityUser &&
    (utilityUser.roles.includes(UTILITY_USER_ROLE_ADMIN) ||
      utilityUser.roles.includes(UTILITY_USER_ROLE_PARTNER));

  const userName =
    currentUser?.name || (utilityUser && [utilityUser.firstName, utilityUser.lastName].join(' '));

  const canSeeOtherPartnerPages =
    hasRolePartnerPropertyManager(utilityUser?.roles || []) ||
    hasRoleGroupAdmin(utilityUser?.roles || []);

  useEffect(() => {
    if (shouldScrollToTop) {
      if (process.browser) {
        global.scrollTo(0, 0);
      }
    }
  }, [step, shouldScrollToTop]);

  const backArrowDOM = onBack ? (
    navBarTheme && navBarTheme === SR_PARTNER_PAGE_NAVBAR_THEME_ROUNDED ? (
      <div styleName="backArrow" onClick={onBack}>
        <div styleName="backArrowIcon" />
        <SrText font="greycliff" weight="700" fontSize="15px">
          Back
        </SrText>
      </div>
    ) : (
      <div styleName="backArrow" onClick={onBack} />
    )
  ) : backUrl ? (
    <SrLink to={backUrl} redirectToLastLocation={redirectToLastLocation} restoreScrollPosition>
      <div styleName="backArrow" />
    </SrLink>
  ) : null;

  const navbarImageDOM = navbarImage ? (
    navbarImageLink && location.pathname.replace(/\/$/, '') !== navbarImageLink ? (
      <SrLink to={navbarImageLink} restoreScrollPosition>
        <SrBox
          backgroundImage={`url(${navbarImage})`}
          backgroundSize="contain"
          backgroundRepeat="no-repeat"
          backgroundPosition="center"
          height="44px"
          width="100%"
        />
      </SrLink>
    ) : (
      <SrBox
        backgroundImage={`url(${navbarImage})`}
        backgroundSize="contain"
        backgroundRepeat="no-repeat"
        backgroundPosition="center"
        height="44px"
        width="100%"
      />
    )
  ) : navbarTitle ? (
    <SrBox
      maxWidth={
        isMediumScreen ? 'calc(100vw - 150px)' : `calc(100vw - (100vw - ${maxWidth}) - 150px)`
      }
    >
      <span styleName="navbarTitle">{navbarTitle}</span>
    </SrBox>
  ) : null;

  return (
    <div
      className={className}
      styleName={classnames('SrPartnerPageV2', SR_PARTNER_PAGE_THEME_TO_CLASSNAME_MAPPING[theme], {
        hasProgressBar: Boolean(progressBarPercent),
        hasWatermark,
        navbarLinks,
      })}
      style={{ padding }}
    >
      <div
        className="SrPartnerPageV2-navBar"
        styleName={classnames(
          SR_PARTNER_PAGE_NAVBAR_THEMES.includes(navBarTheme)
            ? SR_PARTNER_PAGE_NAVBAR_THEME_TO_CLASSNAME_MAPPING[navBarTheme]
            : 'navBar',
        )}
      >
        <div
          className="SrPartnerPageV2-navBarContent"
          styleName="navBarContent"
          style={{ maxWidth: navbarMaxWidth }}
        >
          {!navbarLinks && (
            <>
              {backArrowDOM || <SrBox width="40px" marginLeft="15px" />}
              {navbarImageDOM || <SrBox width="40px" marginLeft="15px" />}
            </>
          )}

          {navbarLinks && (
            <SrBox>
              <SrFlex styleName="navBarMenuItems">
                {backArrowDOM}
                {navbarImageDOM && (
                  <SrBox display="inlineBlock" width="175px" paddingRight="15px">
                    {navbarImageDOM}
                  </SrBox>
                )}
                {navbarLinks.map(({ label, value, icon, target }) => (
                  <SrLink to={value} key={value}>
                    <SrBox
                      key={value}
                      padding="10px 15px"
                      borderRadius="14px"
                      styleName={classnames('navBarMenuItem', {
                        active: value === location.pathname.replace(/\/$/, ''),
                      })}
                      cursor="pointer"
                      target={target}
                    >
                      {icon && (
                        <SrBox position="relative">
                          <SrBox
                            position="absolute"
                            top={`${icon.offset.top}px`}
                            right={`${icon.offset.right}px`}
                            zIndex="1000"
                          >
                            <img
                              src={icon.url}
                              width={icon.width}
                              height={icon.height}
                              alt="Navbar Icon"
                            />
                          </SrBox>
                        </SrBox>
                      )}
                      <SrText font="greycliff" size="16" weight="800">
                        {label}
                      </SrText>
                    </SrBox>
                  </SrLink>
                ))}
              </SrFlex>
            </SrBox>
          )}

          {navBarActionButton && <SrBox marginLeft="15px">{navBarActionButton}</SrBox>}

          {showAccountInfo ? (
            <SrBox>
              <div styleName="SrPartnerPageNavbarDropdown">
                <div styleName="downArrow" />
                <div styleName="userContainer">
                  <span styleName="userName">{userName}</span>
                  <span styleName="userRoles">Account Settings</span>
                </div>
                <div styleName="userNameCircle">
                  <span>{getUserNameInitials(userName)}</span>
                </div>

                <div styleName="menu">
                  {currentUser?.roles.includes(USER_ROLE_ADMIN) && currentPartner?.slug && (
                    <SrLink to={`/partner/${currentPartner.slug}/billing`}>
                      <span styleName="option info first">
                        <span>Billing</span>
                      </span>
                    </SrLink>
                  )}
                  {currentPartner?.slug && (
                    <SrLink to={`/partner/${currentPartner.slug}/settings`}>
                      <span styleName="option danger first">
                        <span>Settings</span>
                      </span>
                    </SrLink>
                  )}
                  {utilityPartner?.utilityPartnerGroupId && canSeeOtherPartnerPages && (
                    <SrPartnerGroupAdminLinks
                      utilityUser={utilityUser}
                      utilityPartnerGroupId={utilityPartner.utilityPartnerGroupId}
                    />
                  )}
                  {showUtilityPartnerSettings && (
                    <SrLink to={`/utility-profit/${utilityPartner.slug}/settings`}>
                      <span styleName="option info first">
                        <span>Settings</span>
                      </span>
                    </SrLink>
                  )}
                  <SrLink
                    to={`/logout${showUtilityPartnerSettings ? '?next=/utility-profit' : ''}`}
                  >
                    <span styleName="option danger">
                      <span>Logout</span>
                    </span>
                  </SrLink>
                </div>
                {currentPartner && (
                  <SrBox position="absolute" left="-100px" top="-4px" height="40px">
                    <SrViewAsPartnerToggle partnerId={currentPartner.id} />
                  </SrBox>
                )}
              </div>
            </SrBox>
          ) : (
            !navBarActionButton && <SrBox width="40px" marginLeft="15px" />
          )}
        </div>
      </div>
      {progressBarPercent && (
        <SrProgressBarPercent
          percent={progressBarPercent}
          text={progressBarText}
          theme={SR_PROGRESS_BAR_THEME_YELLOW}
        />
      )}

      <SrBox maxWidth={maxWidth} margin="0 auto" padding={contentPadding || '0 10px'}>
        {pageHeader && (
          <SrBox margin={isLargeScreen ? '0 15px 33px 15px' : '0 0 21px 0'}>
            <div
              styleName={classnames(
                'pageHeader',
                { hasProgressBar: Boolean(progressBarPercent) },
                SR_PARTNER_PAGE_V2_FONT_SIZE_TO_CLASSNAME_MAPPING[headerSize],
              )}
            >
              {pageHeader}
            </div>
          </SrBox>
        )}
        {pageSubtitle && (
          <SrBox margin={isLargeScreen ? '-19px 15px 25px 15px' : '0 0 27px 0'}>
            <SrText
              font="greycliff"
              fontSize="16px"
              lineHeight="25px"
              color="blueGreycliff"
              opacity={typeof pageSubtitle === 'string' ? 0.6 : null}
              weight="500"
              letterSpacing="0px"
            >
              {pageSubtitle}
            </SrText>
          </SrBox>
        )}
        {children}
      </SrBox>
      {hasWatermark && (
        <SrBox margin={isLargeScreen ? '100px auto 0' : '200px auto 0'}>
          <SrFlex justify="center">
            <SrBox
              width="160px"
              height="100px"
              backgroundImage="url(/images/sunroom-watermark-grey.svg)"
              backgroundPosition="center top"
              backgroundSize="contain"
              backgroundRepeat="no-repeat"
            />
          </SrFlex>
        </SrBox>
      )}
    </div>
  );
};

SrPartnerPageV2.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  backUrl: PropTypes.string,
  onBack: PropTypes.func,
  navbarImage: PropTypes.string,
  navbarImageLink: PropTypes.string,
  navbarTitle: PropTypes.string,
  navbarLinks: PropTypes.array,
  pageHeader: PropTypes.string,
  pageSubtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  progressBarPercent: PropTypes.number,
  progressBarText: PropTypes.string,
  theme: PropTypes.oneOf(SR_PARTNER_PAGE_THEMES),
  step: PropTypes.number,
  shouldScrollToTop: PropTypes.bool,
  padding: PropTypes.string,
  contentPadding: PropTypes.string,
  maxWidth: PropTypes.string,
  navBarTheme: PropTypes.oneOf(SR_PARTNER_PAGE_NAVBAR_THEMES),
  navbarMaxWidth: PropTypes.string,
  navBarActionButton: PropTypes.node,
  headerSize: PropTypes.oneOf(SR_PARTNER_PAGE_V2_FONT_SIZES),
  hasWatermark: PropTypes.bool,
  currentUser: PropTypes.object,
  currentPartner: PropTypes.object,
  utilityUser: PropTypes.object,
  utilityPartner: PropTypes.object,
  redirectToLastLocation: PropTypes.bool,
};

SrPartnerPageV2.defaultProps = {
  maxWidth: '895px',
  navbarMaxWidth: '895px',
  headerSize: SR_PARTNER_PAGE_V2_FONT_SIZE_MEDIUM,
  hasWatermark: false,
  redirectToLastLocation: true,
};

SrPartnerPageV2.displayName = 'SrPartnerPageV2';

export default SrPartnerPageV2;
